import { useState, useRef, useEffect } from 'react'
import type { ChatMessage } from '@/lib/api'
import { api } from '@/lib/api'
import { MessageBubble } from './MessageBubble'
import { GlassButton } from '@/components/ui/GlassButton'
import { Send } from 'lucide-react'
import { useT } from '@/i18n/provider'

interface ChatWindowProps {
  agentId: string
  agentName: string
}

export function ChatWindow({ agentId, agentName }: ChatWindowProps) {
  const t = useT()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [streamingContent, setStreamingContent] = useState('')
  const bottomRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streamingContent])

  async function sendMessage() {
    const text = input.trim()
    if (!text || streaming) return

    const userMsg: ChatMessage = { role: 'user', content: text }
    setMessages((prev) => [...prev, userMsg])
    setInput('')
    setStreaming(true)
    setStreamingContent('')

    try {
      const res = await api.chat.stream(agentId, text, messages)
      if (!res.ok || !res.body) {
        throw new Error(String(res.status))
      }

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let accumulated = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value, { stream: true })
        for (const line of chunk.split('\n')) {
          if (!line.startsWith('data: ')) continue
          const data = line.slice(6).trim()
          if (data === '[DONE]') break
          try {
            const parsed = JSON.parse(data)
            if (parsed.content) {
              accumulated += parsed.content
              setStreamingContent(accumulated)
            }
          } catch {
            // Ignore malformed
          }
        }
      }

      setMessages((prev) => [...prev, { role: 'assistant', content: accumulated }])
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `${t('chat.errorPrefix')}: ${err instanceof Error ? err.message : t('chat.genericError')}` },
      ])
    } finally {
      setStreaming(false)
      setStreamingContent('')
      textareaRef.current?.focus()
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-4 px-2 space-y-4 min-h-0">
        {messages.length === 0 && !streaming && (
          <div className="text-center text-white/30 text-sm py-16">
            <p>{t('chat.startConversationWith', { agentName })}</p>
          </div>
        )}

        {messages.map((msg, i) => (
          <MessageBubble key={i} message={msg} />
        ))}

        {streaming && (
          <MessageBubble
            message={{ role: 'assistant', content: streamingContent }}
            streaming
          />
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="flex-shrink-0 border-t border-white/10 pt-4">
        <div className="flex gap-3 items-end">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('chat.messagePlaceholder', { agentName })}
            disabled={streaming}
            rows={1}
            className="flex-1 glass px-4 py-2.5 text-white text-sm placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[var(--oc-accent)]/60 resize-none disabled:opacity-50 transition-all"
            style={{ minHeight: '44px', maxHeight: '140px' }}
            onInput={(e) => {
              const t = e.target as HTMLTextAreaElement
              t.style.height = 'auto'
              t.style.height = `${Math.min(t.scrollHeight, 140)}px`
            }}
          />
          <GlassButton
            onClick={sendMessage}
            disabled={!input.trim() || streaming}
            loading={streaming}
            size="md"
          >
            <Send size={15} />
          </GlassButton>
        </div>
      </div>
    </div>
  )
}
